/**
 * Espace partenaire — profil société + pièces de conformité.
 * Le partenaire renseigne ses informations et téléverse ses pièces (KBIS,
 * URSSAF, assurance RC…) en indiquant une date de validité par pièce.
 */
import type {
  PartnerComplianceDoc,
  PartnerComplianceDocType,
  PartnerProfileStatus,
} from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ShieldCheck, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { uploadToPresignedUrl } from '@/features/documents/documents-api';
import {
  getMyComplianceDownloadUrl,
  getMyPartnerProfile,
  requestComplianceUpload,
  upsertMyPartnerProfile,
} from '@/features/partners/partners-api';
import { extractErrorMessage } from '@/features/auth/error-message';

const docTypeLabels: Record<PartnerComplianceDocType, string> = {
  kbis: 'KBIS',
  attestation_urssaf: 'Attestation URSSAF',
  assurance_rc: 'Assurance RC pro',
  statuts: 'Statuts',
  rib: 'RIB',
  piece_identite_dirigeant: 'Pièce identité dirigeant',
  autre: 'Autre',
};

const docTypeOptions = Object.entries(docTypeLabels) as [
  PartnerComplianceDocType,
  string,
][];

const profileStatusBadge: Record<PartnerProfileStatus, { label: string; cls: string }> = {
  incomplet: { label: 'Incomplet', cls: 'bg-gray-100 text-gray-700' },
  en_verification: { label: 'En vérification', cls: 'bg-amber-100 text-amber-800' },
  valide: { label: 'Validé', cls: 'bg-green-100 text-green-800' },
  suspendu: { label: 'Suspendu', cls: 'bg-red-100 text-red-800' },
};

const docStatusBadge: Record<PartnerComplianceDoc['status'], { label: string; cls: string }> = {
  en_attente: { label: 'En attente', cls: 'bg-gray-100 text-gray-700' },
  valide: { label: 'Validé', cls: 'bg-green-100 text-green-800' },
  refuse: { label: 'Refusé', cls: 'bg-red-100 text-red-800' },
  expire: { label: 'Expiré', cls: 'bg-orange-100 text-orange-800' },
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR');
}

export function PartnerProfilePage() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['partner', 'me'], queryFn: getMyPartnerProfile });

  const [profileForm, setProfileForm] = useState({
    companyName: '',
    siret: '',
    legalForm: '',
    address: '',
  });
  const [profileLoadedId, setProfileLoadedId] = useState<string | null>(null);
  // Hydrate le formulaire au premier chargement du profil.
  const profile = data.data?.profile ?? null;
  if (profile && profile.id !== profileLoadedId) {
    setProfileLoadedId(profile.id);
    setProfileForm({
      companyName: profile.companyName,
      siret: profile.siret ?? '',
      legalForm: profile.legalForm ?? '',
      address: profile.address ?? '',
    });
  }

  const saveProfile = useMutation({
    mutationFn: () =>
      upsertMyPartnerProfile({
        companyName: profileForm.companyName,
        siret: profileForm.siret || null,
        legalForm: profileForm.legalForm || null,
        address: profileForm.address || null,
      }),
    onSuccess: () => {
      toast.success('Profil enregistré');
      qc.invalidateQueries({ queryKey: ['partner', 'me'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const [uploadForm, setUploadForm] = useState({
    docType: 'kbis' as PartnerComplianceDocType,
    issuedAt: '',
    expiresAt: '',
  });
  const fileRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { uploadUrl } = await requestComplianceUpload({
        docType: uploadForm.docType,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        issuedAt: uploadForm.issuedAt || null,
        expiresAt: uploadForm.expiresAt || null,
      });
      setProgress(0);
      await uploadToPresignedUrl(uploadUrl, file, setProgress);
    },
    onSuccess: () => {
      toast.success('Pièce téléversée');
      if (fileRef.current) fileRef.current.value = '';
      setUploadForm({ docType: 'kbis', issuedAt: '', expiresAt: '' });
      qc.invalidateQueries({ queryKey: ['partner', 'me'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
    onSettled: () => setProgress(null),
  });

  async function download(documentId: string) {
    try {
      const url = await getMyComplianceDownloadUrl(documentId);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  }

  const status = profile?.status ?? 'incomplet';
  const docs = data.data?.complianceDocs ?? [];

  return (
    <DashboardLayout variant="partenaire" title="Mon profil">
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-bcl-blue" />
              Conformité
            </CardTitle>
            <CardDescription>
              Statut de votre dossier partenaire.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Badge variant="outline" className={profileStatusBadge[status].cls}>
              {profileStatusBadge[status].label}
            </Badge>
            <p className="mt-3 text-sm text-gray-500">
              Vos pièces (KBIS, URSSAF, assurance RC…) doivent rester à jour.
              Vous recevrez un rappel avant chaque expiration.
            </p>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Informations société</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="companyName">Raison sociale</Label>
              <Input
                id="companyName"
                value={profileForm.companyName}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, companyName: e.target.value })
                }
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="siret">SIRET</Label>
                <Input
                  id="siret"
                  value={profileForm.siret}
                  onChange={(e) =>
                    setProfileForm({ ...profileForm, siret: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="legalForm">Forme juridique</Label>
                <Input
                  id="legalForm"
                  value={profileForm.legalForm}
                  onChange={(e) =>
                    setProfileForm({ ...profileForm, legalForm: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="address">Adresse</Label>
              <Input
                id="address"
                value={profileForm.address}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, address: e.target.value })
                }
              />
            </div>
            <Button
              disabled={!profileForm.companyName || saveProfile.isPending}
              onClick={() => saveProfile.mutate()}
            >
              {saveProfile.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Pièces de conformité</CardTitle>
          <CardDescription>
            Téléversez vos pièces et renseignez leur date de validité.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="docType">Type de pièce</Label>
              <select
                id="docType"
                value={uploadForm.docType}
                onChange={(e) =>
                  setUploadForm({
                    ...uploadForm,
                    docType: e.target.value as PartnerComplianceDocType,
                  })
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {docTypeOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="issuedAt">Date d'émission</Label>
              <Input
                id="issuedAt"
                type="date"
                value={uploadForm.issuedAt}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, issuedAt: e.target.value })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="expiresAt">Date de validité</Label>
              <Input
                id="expiresAt"
                type="date"
                value={uploadForm.expiresAt}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, expiresAt: e.target.value })
                }
              />
            </div>
            <div className="flex items-end">
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) upload.mutate(file);
                }}
              />
              <Button
                variant="outline"
                className="w-full"
                disabled={upload.isPending}
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="mr-2 h-4 w-4" />
                {progress !== null ? `Envoi… ${progress}%` : 'Téléverser'}
              </Button>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pièce</TableHead>
                <TableHead>Validité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.length ? (
                docs.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">
                      {docTypeLabels[doc.docType]}
                      <span className="block text-xs text-gray-500">{doc.filename}</span>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(doc.expiresAt)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={docStatusBadge[doc.status].cls}>
                        {docStatusBadge[doc.status].label}
                      </Badge>
                      {doc.status === 'refuse' && doc.reviewReason && (
                        <span className="block text-xs text-red-600">
                          {doc.reviewReason}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => download(doc.id)}
                        aria-label="Télécharger"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-gray-500">
                    Aucune pièce déposée.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
